import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import { Trash2, Loader2, ArrowLeft, CreditCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    adresse: "",
    livraison: "postal",
    payment_method: "stripe",
  });

  useEffect(() => {
    const savedCart = localStorage.getItem("nymphea_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(productId);
      return;
    }
    const newCart = cart.map((item) =>
      item.product_id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCart(newCart);
    localStorage.setItem("nymphea_cart", JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cart.filter((item) => item.product_id !== productId);
    setCart(newCart);
    localStorage.setItem("nymphea_cart", JSON.stringify(newCart));
  };

  const total = cart.reduce(
    (acc, item) => acc + item.product.prix * item.quantity,
    0
  );

  const shippingCost = formData.livraison === "postal" && total < 30 ? 4.90 : 0;
  const finalTotal = total + shippingCost;

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.email) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    
    if (formData.livraison === "postal" && !formData.adresse) {
      toast.error("Veuillez entrer votre adresse de livraison.");
      return;
    }
    
    setLoading(true);

    try {
      // Create order first
      const orderRes = await axios.post(`${API}/orders`, {
        items: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.product.prix,
          name: item.product.nom,
        })),
        nom: formData.nom,
        email: formData.email,
        adresse: formData.livraison === "postal" ? formData.adresse : null,
        livraison: formData.livraison,
        payment_method: formData.payment_method,
      });

      if (formData.payment_method === "stripe") {
        // Create Stripe checkout session
        const checkoutRes = await axios.post(`${API}/checkout/stripe`, {
          origin_url: window.location.origin,
          payment_type: "order",
          reference_id: orderRes.data.id,
          email: formData.email,
        });

        localStorage.removeItem("nymphea_cart");
        window.location.href = checkoutRes.data.url;
      } else {
        // PayPal - create hosted checkout and redirect
        const paypalRes = await axios.post(`${API}/checkout/paypal/hosted`, {
          origin_url: window.location.origin,
          payment_type: "order",
          reference_id: orderRes.data.id,
          email: formData.email,
        });

        localStorage.removeItem("nymphea_cart");
        window.location.href = paypalRes.data.url;
      }
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center page-container py-24">
          <h1 className="text-2xl font-heading mb-4">Votre panier est vide</h1>
          <p className="text-muted-foreground mb-8">
            Découvrez notre boutique pour trouver des créations à emporter.
          </p>
          <Link to="/shop">
            <Button className="btn-primary">Voir la boutique</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <section className="pt-32 pb-24 md:pb-32">
        <div className="page-container">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Continuer les achats
          </Link>

          <h1 className="text-3xl md:text-4xl font-heading font-light mb-12">
            Finaliser la commande
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Cart Summary */}
            <div>
              <h2 className="text-lg font-heading border-b border-border/40 pb-2 mb-6">
                Votre panier
              </h2>

              <div className="space-y-6">
                {cart.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex gap-4 pb-6 border-b border-border/40"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={item.product.image_url}
                      alt={item.product.nom}
                      className="w-20 h-20 object-cover border border-border/40"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">{item.product.nom}</h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        {item.product.prix.toFixed(2)}€
                      </p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="w-7 h-7 border border-border flex items-center justify-center hover:bg-secondary text-sm"
                        >
                          -
                        </button>
                        <span className="text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="w-7 h-7 border border-border flex items-center justify-center hover:bg-secondary text-sm"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          className="ml-auto text-muted-foreground hover:text-destructive"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <p className="font-medium">
                      {(item.product.prix * item.quantity).toFixed(2)}€
                    </p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span>{total.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span>
                    {shippingCost === 0
                      ? formData.livraison === "atelier"
                        ? "Gratuit (retrait)"
                        : "Offert"
                      : `${shippingCost.toFixed(2)}€`}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-medium pt-4 border-t border-border/40">
                  <span>Total</span>
                  <span>{finalTotal.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handlePayment} className="space-y-8">
              {/* Contact */}
              <div className="space-y-4">
                <h2 className="text-lg font-heading border-b border-border/40 pb-2">
                  Vos coordonnées
                </h2>
                <div className="form-group">
                  <Label htmlFor="nom" className="form-label">Nom / Prénom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    className="form-input"
                    data-testid="checkout-nom"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="form-input"
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              {/* Delivery */}
              <div className="space-y-4">
                <h2 className="text-lg font-heading border-b border-border/40 pb-2">
                  Mode de livraison
                </h2>
                <RadioGroup
                  value={formData.livraison}
                  onValueChange={(v) => setFormData({ ...formData, livraison: v })}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="postal" id="postal" data-testid="delivery-postal" />
                    <Label htmlFor="postal" className="cursor-pointer flex-1">
                      <span className="font-medium">Envoi postal</span>
                      <span className="text-sm text-muted-foreground block">
                        {total >= 30 ? "Offert" : "4,90€"} — 5-7 jours ouvrés
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="atelier" id="atelier" data-testid="delivery-atelier" />
                    <Label htmlFor="atelier" className="cursor-pointer flex-1">
                      <span className="font-medium">Remise en main propre</span>
                      <span className="text-sm text-muted-foreground block">
                        Gratuit — Récupérez votre commande directement à l'Atelier La Peauésie
                      </span>
                    </Label>
                  </div>
                </RadioGroup>

                {formData.livraison === "postal" && (
                  <div className="form-group pt-2">
                    <Label htmlFor="adresse" className="form-label">Adresse de livraison *</Label>
                    <Textarea
                      id="adresse"
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                      required
                      placeholder="Adresse complète..."
                      className="form-textarea"
                      data-testid="checkout-adresse"
                    />
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="space-y-4">
                <h2 className="text-lg font-heading border-b border-border/40 pb-2">
                  Paiement
                </h2>
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                    <Label htmlFor="stripe" className="cursor-pointer flex-1">
                      <span className="font-medium flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Carte bancaire
                      </span>
                      <span className="text-sm text-muted-foreground block">
                        Paiement sécurisé via Stripe
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                    <Label htmlFor="paypal" className="cursor-pointer flex-1">
                      <span className="font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19a.641.641 0 0 0-.633.545l-1.537 9.46zm2.054-15.004l-1.18 7.461h2.18c2.75 0 4.867-.87 5.516-4.213.232-1.195.103-2.083-.386-2.696-.543-.68-1.562-1.018-3.107-1.018h-2.39a.641.641 0 0 0-.633.466z"/>
                        </svg>
                        PayPal
                      </span>
                      <span className="text-sm text-muted-foreground block">
                        Paiement via votre compte PayPal
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="checkout-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirection vers {formData.payment_method === "stripe" ? "Stripe" : "PayPal"}...
                  </>
                ) : (
                  `Payer ${finalTotal.toFixed(2)}€ via ${formData.payment_method === "stripe" ? "carte" : "PayPal"}`
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                En passant commande, vous acceptez nos conditions générales de vente.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
